import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Box, Button, Flex, Spinner, Text } from 'theme-ui'
import { useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import { useWaitList } from '../../ListPage'
import {
  AddToCartProductListVariants,
  AddToCartProductListQuantity,
  AddToCartProductListFrequency,
} from './index'
import { useAddToCart } from '~/hooks/utils/useAddToCart'
import useMatchMedia from '~/hooks/utils/use-match-media'
import useVariantAvailability from '~/hooks/components/use-variant-availability'
import { ChordCartContext } from '~/components/Mini-Cart/CartContext'
import { experiments, useExperiment } from '~/hooks/utils/use-experiment'

export const AddToCartProductList = ({
  product,
  setOpenAtc,
  openAtc,
  collectionName,
}) => {
  const { variants = [], subscription, isGoods, name } = product

  const allVariantOptions = useMemo(
    () => variants.filter(v => v.optionValues).flatMap(v => v.optionValues),
    [variants],
  )

  const normalizeAllVariantOptionsForGoods = useMemo(() => {
    const filteredOptions = allVariantOptions.reduce(
      (accumulator, currentValue) => {
        const optionSlug = currentValue.option_type[0].slug
        if (!accumulator[optionSlug]) {
          accumulator[optionSlug] = []
        }
        if (
          !accumulator[optionSlug].some(obj => obj.slug === currentValue.slug)
        ) {
          accumulator[optionSlug].push(currentValue)
        }
        return accumulator
      },
      {},
    )

    return Object.keys(filteredOptions).reduce((accumulator, key) => {
      accumulator[key] = {
        objects: filteredOptions[key],
      }
      return accumulator
    }, {})
  }, [allVariantOptions])

  const keyForGoods = Object.keys(normalizeAllVariantOptionsForGoods)

  const firstVariantOptions = useMemo(() => {
    const options = {}
    if (variants[0].optionValues) {
      variants[0].optionValues.forEach(optionObj => {
        options[optionObj.option_type[0].slug] = optionObj.slug
      })
    }
    return options
  }, [variants])

  const variantHasDiscountPromo = variants.find(v => !!v.discountPercentage)

  const [quantity, setQuantity] = useState(1)
  const [variantSelected, setVariantSelected] = useState(firstVariantOptions)
  const [buttonIsLoading, setButtonIsLoading] = useState(false)
  const [apiError, setApiError] = useState(false)
  const [subscriptionValue, setSubscriptionValue] = useState(
    variantHasDiscountPromo
      ? 'one-time-purchase'
      : subscription?.slug || 'one-time-purchase',
  )
  const { addSubscription, addToCart } = useAddToCart()
  const mobile = useMatchMedia('(max-width: 767px)')
  const translate = useTranslate()
  const { handleDataWaitlist } = useWaitList()

  const variantsAvailable = variants.filter(({ optionValues }) =>
    keyForGoods.length > 1
      ? keyForGoods.some(key =>
          optionValues.some(opt => opt.slug === variantSelected[key]),
        )
      : true,
  )

  const handleVariantSelected = useCallback(() => {
    if (variants.every(v => !v.optionValues)) {
      return variants[0]
    } else {
      return variants.find(v =>
        v.optionValues?.every(
          value => variantSelected[value.option_type[0].slug] === value.slug,
        ),
      )
    }
  }, [variantSelected, variants])

  const currentVariant = handleVariantSelected() || {}
  const { regularPrice, sku, soldOut } = currentVariant
  const { isAvailable } = useVariantAvailability({ sku })
  const outOfStock = !isAvailable || product.hidden

  const discountPercentage = currentVariant?.discountPercentage
  const discountToSubscription = currentVariant?.discountToSubscription

  // To show chord promo pricing on PDP based on the % set in contentful
  // % should MATCH the promo rules on OMS
  const promoDiscountPrice = discountPercentage
    ? regularPrice - (discountPercentage / 100) * regularPrice
    : null

  // check if the CMS discount on subscription is higher than regular subscription discount (15%)
  const hasHigherDiscountThanSub =
    discountToSubscription &&
    discountPercentage > subscription?.discountPercentage

  const subBtnPrice =
    regularPrice *
    (1 -
      (hasHigherDiscountThanSub
        ? discountPercentage
        : subscription?.discountPercentage ?? 0) /
        100)

  const oneTimeBtnPrice = discountPercentage ? promoDiscountPrice : regularPrice

  const isSubscription = subscriptionValue === subscription?.slug

  const btnPriceUnit = isSubscription ? subBtnPrice : oneTimeBtnPrice

  const btnPrice = (btnPriceUnit * quantity).toFixed(2)

  const { lineItems } = useContext(ChordCartContext)

  const handleAddToCard = async () => {
    setApiError(false)

    if (openAtc !== name) {
      setOpenAtc(name)
      return
    }

    setButtonIsLoading(true)

    if (soldOut) {
      setButtonIsLoading(false)
      return
    }

    if (outOfStock) {
      handleDataWaitlist(currentVariant, product)
      setButtonIsLoading(false)
      return
    }

    const preFilledBundle = currentVariant?.usePreFilledBundle
    const finalSku = preFilledBundle ? preFilledBundle.masterSku : sku

    const variantInCart = lineItems?.find(
      lineItem => lineItem.variant.sku === currentVariant.sku,
    )

    const variantHasSubscriptionInCart =
      variantInCart?.subscriptionLineItems?.length > 0

    const { intervals } = subscription || {}
    const interval = intervals?.[0] ?? null

    try {
      if (
        subscriptionValue === 'subscribe-and-save' &&
        (variantHasSubscriptionInCart || !variantInCart)
      ) {
        await addSubscription({ sku: finalSku, quantity, interval })
      } else if (
        subscriptionValue === 'one-time-purchase' &&
        !variantHasSubscriptionInCart
      ) {
        await addToCart(finalSku, quantity)
      } else {
        throw new Error(translate('error.subscription.default'))
      }

      setOpenAtc('')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
      setApiError(true)
    } finally {
      setButtonIsLoading(false)
    }
  }

  const collectionsCardsTest = useExperiment(experiments.newProductCardLayout)

  const isShop =
    typeof window !== 'undefined' ? location.pathname.includes('/shop') : false
  return (
    <>
      <Box
        sx={{
          maxWidth: '400px',
          width: '100%',
          /*border: !collectionsCardsTest && 'solid 1px var(--theme-ui-colors-mac-primary)',*/
          borderRadius: '8px',
          overflow: 'hidden',
        }}
        margin="auto"
        /*marginTop={collectionsCardsTest ? 'auto' : 20}*/
        marginTop={'auto'}
      >
        <Box
          sx={{
            maxHeight: openAtc !== name ? '0px' : '400px',
            overflow: 'hidden',
            transition: 'max-height 0.5s ease-in-out',
            color: isShop ? 'primary' : 'var(--theme-ui-colors-mac-primary)',
          }}
        >
          {isGoods ? (
            <Flex
              sx={{
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: ['8px', '16px'],
                /*px: collectionsCardsTest && ['8px'],*/
                px: ['8px'],
                /*padding: !collectionsCardsTest && ['10px 15px', '16px 38.5px'],*/
                flexWrap: 'wrap',
              }}
            >
              {product.collection.some(c => c.slug === 'gifts') &&
                !!allVariantOptions.length && (
                  <AddToCartProductListVariants
                    allVariantOptions={allVariantOptions}
                    setVariantSelected={setVariantSelected}
                    variantSelected={variantSelected}
                    label="Amount"
                    sx={{
                      flexDirection: ['column', 'row'],
                      '> div': {
                        flexWrap: ['wrap', 'unset'],
                        '> button': {
                          width: ['47.5%', 'unset'],
                        },
                      },
                    }}
                  />
                )}

              {product.collection.some(c => c.slug === 'goods') && (
                <Flex
                  sx={{
                    gap: ['8px', '25px'],
                    flexDirection: ['column', 'row'],
                    width: ['100%', 'unset'],
                  }}
                >
                  {keyForGoods?.map(name => (
                    <AddToCartProductListVariants
                      allVariantOptions={
                        normalizeAllVariantOptionsForGoods[name].objects
                      }
                      setVariantSelected={setVariantSelected}
                      variantSelected={variantSelected}
                      variants={variants}
                      variantsAvailable={variantsAvailable}
                      isSelect={true}
                      label={name === 'merch-size' ? 'size' : name}
                      sx={{
                        /*gap: collectionsCardsTest ? '5px' : '0px',*/
                        gap: '5px',
                      }}
                      key={name}
                    />
                  ))}
                </Flex>
              )}

              {!mobile && (
                <Text
                  as="span"
                  sx={{
                    fontWeight: '900',
                    fontSize: ['12px', '18px'],
                  }}
                >
                  Quantity:
                </Text>
              )}

              <AddToCartProductListQuantity
                setQuantity={setQuantity}
                quantity={quantity}
                sx={{
                  mr: 'auto',
                  mb: '16px',
                }}
              />
            </Flex>
          ) : (
            <Flex
              sx={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                /*gap: collectionsCardsTest ? '8px' : ['8px', '16px'],*/
                gap: '8px',
                /*px: collectionsCardsTest && '8px',*/
                px: '8px',
                /*padding: !collectionsCardsTest && ['10px 15px', '16px 38.5px'],*/
                flexWrap: 'wrap',
              }}
            >
              <AddToCartProductListVariants
                allVariantOptions={allVariantOptions}
                setVariantSelected={setVariantSelected}
                variantSelected={variantSelected}
                label={`${!mobile ? 'Pack' : ''} Size`}
              />

              <AddToCartProductListQuantity
                setQuantity={setQuantity}
                quantity={quantity}
              />

              {subscription && (
                <AddToCartProductListFrequency
                  subscriptionValue={subscriptionValue}
                  setSubscriptionValue={setSubscriptionValue}
                  subscriptionData={subscription}
                  promoDiscountPrice={promoDiscountPrice}
                  discountPercentage={discountPercentage}
                  discountToSubscription={discountToSubscription}
                  regularPrice={regularPrice.toFixed(2)}
                  variants={variants}
                  collectionName={collectionName}
                />
              )}
            </Flex>
          )}
        </Box>

        <Button
          disabled={openAtc && soldOut}
          sx={{
            borderRadius: '0px',
            alignItems: 'center',
            color: 'var(--theme-ui-colors-mac-secondary,#FFDD00)',
            justifyContent: 'center',
            display: 'flex',
            /*fontSize: collectionsCardsTest ? ['14px', '20px'] : ['16px', '20px'],*/
            fontSize: ['14px', '20px'],
            /*fontWeight: collectionsCardsTest && ['400', '700'],*/
            fontWeight: ['400', '700'],
            width: '100%',
            height: ['40px', '56px'],
            fontFamily: 'Sofia',
            background: 'var(--theme-ui-colors-mac-primary, #2B38BD)',
            '&:hover': {
              background: 'var(--theme-ui-colors-mac-primary, #2B38BD)',
              color: 'var(--theme-ui-colors-mac-secondary,#FFDD00)',
            },
            px: '0',
          }}
          onClick={handleAddToCard}
        >
          {buttonIsLoading ? (
            <Spinner data-testid="spinner" size="20" color="inherit" />
          ) : openAtc === name && (outOfStock || soldOut) ? (
            soldOut ? (
              translate('product.sold_out')
            ) : (
              translate('product.join_waitlist')
            )
          ) : (
            `${translate('product.add_to_cart')} - $${btnPrice}`
          )}
        </Button>
      </Box>
      {apiError && openAtc === name && (
        <Text
          color="error"
          sx={{
            maxWidth: '400px',
            width: '100%',
            margin: 'auto',
            textAlign: 'center',
          }}
        >
          {translate('error.subscription.default')}
        </Text>
      )}
    </>
  )
}
